import * as React from "react";
import { useNavigate } from "react-router-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import { AuthCompanyContext } from "@homesusa/auth";
import { RoleEmployee, RoleEmployeeLabel } from "@homesusa/core";
import { Button } from "@homesusa/layout";

const defaultRole = "MLS Admin";

interface AnchorEl {
  anchorEl: null | HTMLElement;
  open: boolean;
  handleClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleClose: () => void;
}

const useAnchorEl = (): AnchorEl => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  return { anchorEl, open, handleClick, handleClose };
};

export function SelectEmployeeRoleButton(): JSX.Element {
  const anchorRef = useAnchorEl();
  const { currentEmployee, setEmployeeRole, currentCompany } =
    React.useContext(AuthCompanyContext);
  const navigate = useNavigate();

  const onClick = (role?: RoleEmployee): void => {
    setEmployeeRole(role);
    anchorRef.handleClose();
    navigate("/");
  };

  if (!currentCompany) {
    return <></>;
  }

  return (
    <React.Fragment>
      <Button
        size="small"
        aria-controls={anchorRef.open ? "select-employee-role" : undefined}
        aria-expanded={anchorRef.open ? "true" : undefined}
        aria-label="select employee role"
        aria-haspopup="menu"
        onClick={anchorRef.handleClick}
        variant="contained"
        endIcon={<ArrowDropDownIcon />}
      >
        {(currentEmployee
          ? RoleEmployeeLabel.get(currentEmployee.roleName)
          : defaultRole) ?? defaultRole}
      </Button>
      <Popper
        sx={{ zIndex: 1 }}
        anchorEl={anchorRef.anchorEl}
        open={anchorRef.open}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom"
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={anchorRef.handleClose}>
                <MenuList id="select-employee-role" autoFocusItem>
                  <MenuItem onClick={(): void => onClick()}>
                    {defaultRole}
                  </MenuItem>
                  {Object.values(RoleEmployee).map((role) => (
                    <MenuItem
                      key={role}
                      value={role}
                      onClick={(): void => onClick(role)}
                    >
                      {RoleEmployeeLabel.get(role as RoleEmployee)}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
}
